<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AnalyticsService from '@/services/analytics'
import HourlyDistributionChart from './charts/HourlyDistributionChart.vue'
import UserBreakdownChart from './charts/UserBreakdownChart.vue'
import { useCompanyStore } from "@/stores/company";

const companyStore = useCompanyStore();

const props = defineProps({
  agentId: {
    type: String,
    required: false
  }
})

const route = useRoute()
const router = useRouter()
const agentId = props.agentId || route.params.agentId

// Top Stats Data
const stats = ref([
    { title: 'Total Conversations', value: '0', subtext: 'All time', color: 'success' },
    { title: 'Average per Day', value: '0', subtext: 'All time', color: 'muted' },
    { title: 'Active Days', value: '0', subtext: 'All time', color: 'muted' },
    { title: 'Unique Users', value: '0', subtext: 'All time', color: 'primary' }
])

const hourlyData = ref(null)
const userBreakdownData = ref(null)
const isLoading = ref(false)
const error = ref(null)

const chartHeight = computed(() => {
    if (hourlyData.value && hourlyData.value.labels) {
        // Minimum 300px, or 50px per item to be comfortable and spacious
        return Math.max(300, hourlyData.value.labels.length * 50) + 'px'
    }
    return '300px'
})

const fetchAgentStats = async () => {
    if (!agentId) return

    try {
        isLoading.value = true
        const currentCompanyId = companyStore.companyId
        
        if (currentCompanyId) {
             const response = await AnalyticsService.getAgentAnalytics(currentCompanyId, agentId)
             
             if (response) {
                 // Update Stats
                 if (response.statistics) {
                     stats.value[0].value = response.statistics.total_conversations
                     stats.value[1].value = response.statistics.avg_conversations_per_day
                     stats.value[2].value = response.statistics.active_days
                     stats.value[3].value = response.statistics.unique_users
                 }

                 // Update Hourly Chart
                 if (response.hourly_distribution) {
                     const hours = response.hourly_distribution.map(item => {
                         const hour = item.hour
                         return hour >= 12 ? `${hour === 12 ? 12 : hour - 12} PM` : `${hour === 0 ? 12 : hour} AM`
                     })
                     const counts = response.hourly_distribution.map(item => item.conversation_count)
                     
                     hourlyData.value = {
                         labels: hours,
                         datasets: [{
                             label: 'Conversations',
                             data: counts,
                             backgroundColor: '#4285F4',
                             borderRadius: 4,
                             barThickness: 20
                         }]
                     }
                 }

                 // Update Device Breakdown Chart
                 if (response.device_breakdown) {
                     // Sort by count and take top 5
                     const sortedDevices = response.device_breakdown.sort((a, b) => b.conversation_count - a.conversation_count).slice(0, 5)

                     const colors = ['#4285F4', '#34A853', '#FBBC05', '#EA4335', '#AB47BC']

                     userBreakdownData.value = {
                         labels: sortedDevices.map(d => d.device_name || (d.user_uuid || '').substring(0, 8) + '...'),
                         datasets: [{
                             data: sortedDevices.map(d => d.conversation_count),
                             backgroundColor: colors.slice(0, sortedDevices.length),
                             hoverOffset: 4
                         }]
                     }
                 }
             }
        }
    } catch (e) {
        console.error('Error fetching agent analytics:', e)
        error.value = 'Failed to load analytics data'
    } finally {
        isLoading.value = false
    }
}

onMounted(() => {
    fetchAgentStats()
})
</script>

<template>
  <div class="w-full p-4 dashboard-container">
    <div class="flex justify-between items-start mb-4 header-row">
      <div class="flex flex-col gap-1">
        <h2 class="h4 font-bold text-white mb-0 flex items-center">
          <i class="bi bi-arrow-left mr-3 cursor-pointer" @click="router.push({ name: 'ConversationsAnalyticsView' })" style="cursor: pointer;"></i>
          <span class="mr-2 chart-icon">📊</span> Agent Analytics
        </h2>
        <p class="header-subtitle">Conversation stats, peak-hour demand, and device breakdown for this agent.</p>
      </div>
      <span v-if="isValidating" class="spinner-sm text-light" role="status" aria-hidden="true"></span>
    </div>

    <div v-if="error" class="alert alert-danger" role="alert">
        {{ error }}
    </div>

    <!-- Top Stats Cards -->
    <div class="grid grid-cols-12 gap-3 mb-4">
      <div v-for="(stat, index) in stats" :key="stat.title || index" class="col-span-12 md:col-span-3">
        <div class="card h-full border-0 shadow-sm rounded-3">
          <div class="card-body">
             <h6 class="card-subtitle mb-2 text-gray-500 small">{{ stat.title }}</h6>
             <h2 class="card-title font-bold mb-1">
                 <span v-if="isLoading" class="spinner-sm" role="status" aria-hidden="true"></span>
                 <span v-else>{{ stat.value }}</span>
             </h2>
             <small :class="`text-${stat.color}`">{{ stat.subtext }}</small>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-12 gap-3">
       <!-- Hourly Distribution -->
       <div class="col-span-12 md:col-span-6">
        <div class="card border-0 shadow-sm rounded-3 h-full">
           <div class="card-body">
            <h5 class="card-title font-bold mb-1">Hourly Distribution</h5>
            <p class="text-gray-500 small mb-3">Peak hours for conversations</p>
             <div style="height: 300px; overflow-y: auto; padding-right: 10px;">
                 <div v-if="hourlyData" :style="{ height: chartHeight, minHeight: '300px' }">
                    <HourlyDistributionChart :data="hourlyData" />
                 </div>
                 <div v-else-if="!isLoading" class="flex items-center justify-center h-full text-gray-500">No data available</div>
             </div>
           </div>
        </div>
      </div>

       <!-- Device Breakdown -->
       <div class="col-span-12 md:col-span-6">
           <div class="card border-0 shadow-sm rounded-3 h-full">
               <div class="card-body">
                   <h5 class="card-title font-bold mb-1">Device Breakdown</h5>
                   <p class="text-gray-500 small mb-3">Conversations by device</p>
                   <div style="height: 300px;">
                      <UserBreakdownChart v-if="userBreakdownData" :data="userBreakdownData" />
                      <div v-else-if="!isLoading" class="flex items-center justify-center h-full text-gray-500">No data available</div>
                   </div>
               </div>
           </div>
       </div>
    </div>
  </div>
</template>

<style scoped>
.dashboard-container {
    background-color: #f8f9fa;
    min-height: 100vh;
}

.header-subtitle { font-size: 12.5px; color: rgba(255,255,255,0.55); margin: 0; line-height: 1.4; }

.header-row {
    background-color: #1a1f36;
    padding: 1.5rem;
    border-radius: 8px;
    margin-left: -12px;
    margin-right: -12px;
    margin-top: -12px;
}

.chart-icon {
    font-size: 1.5rem;
}

.card {
    border: 1px solid rgba(0,0,0,0.05);
}

.text-primary { color: var(--text-primary) !important; }
.text-success { color: var(--text-success) !important; }
.text-gray-500 { color: var(--text-gray-500) !important; }

.card-body {
  padding: 0 !important;
}
</style>
